import React from "react";
import { Box, Typography, Paper } from "@mui/material";

interface EventPreviewCardProps {
  eventTitle: string;
  eventDate: string;
  streamTime: string;
  streamLocation: string;
  locationDetails?: string;
  sponsorName: string;
  sponsorLogo?: string;
  isLiveStream?: boolean;
  backgroundColor?: string;
  accentColor?: string;
}

const EventPreviewCard: React.FC<EventPreviewCardProps> = ({
  eventTitle,
  eventDate,
  streamTime,
  streamLocation,
  locationDetails,
  sponsorName,
  sponsorLogo,
  isLiveStream = true,
  backgroundColor = "#5a5a5a",
  accentColor = "#2d5f4f",
}) => {
  return (
    <Paper
      sx={{
        backgroundColor,
        borderRadius: "16px",
        padding: "16px",
        width: "100%",
        height: "280px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      {/* Top Section */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Event Title */}
        <Typography
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.5rem",
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          {eventTitle}
        </Typography>

        {/* Event Date */}
        <Typography
          sx={{
            color: "#e0e0e0",
            fontWeight: 300,
            fontSize: "0.9rem",
            mb: 1.5,
          }}
        >
          {eventDate}
        </Typography>

        <Box
          sx={{
            backgroundColor: accentColor,
            borderRadius: "6px",
            padding: "6px 12px",
            width: "100%",
            mx: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isLiveStream && (
              <>
                <Box
                  sx={{
                    backgroundColor: "#ff3b30",
                    color: "#fff",
                    px: "4px",
                    py: "2px",
                    borderRadius: "3px",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                  }}
                >
                  LIVE
                </Box>
                <Typography
                  sx={{
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  STREAM
                </Typography>
              </>
            )}
          </Box>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {streamTime}
          </Typography>
        </Box>

        {/* Location */}
        <Box>
          <Typography
            sx={{
              color: "#b0b0b0",
              fontSize: "0.6rem",
              fontWeight: 600,
              letterSpacing: "1px",
              mb: 0.5,
            }}
          >
            BROADCASTING LIVE FROM
          </Typography>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            {streamLocation}
          </Typography>
          {locationDetails && (
            <Typography
              sx={{
                color: "#d0d0d0",
                fontSize: "0.75rem",
              }}
            >
              {locationDetails}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: "70%",
          height: "1px",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          my: 1,
        }}
      />

      {/* Sponsor Section */}
      <Box sx={{ textAlign: "center" }}>
        <Typography
          sx={{
            color: "#a0a0a0",
            fontSize: "0.6rem",
            fontWeight: 600,
            letterSpacing: "1px",
            mb: 0.8,
          }}
        >
          BROADCAST SPONSORED BY
        </Typography>

        {sponsorLogo ? (
          <Box
            component="img"
            src={sponsorLogo}
            alt={sponsorName}
            sx={{
              height: "30px",
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "0.9rem",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            {sponsorName}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default EventPreviewCard;
